// ** React Imports
import React, {ReactNode, useState} from 'react'
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// ** Next Imports
import { Link } from 'react-router-dom'

// ** MUI Components
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { styled, useTheme } from '@mui/material/styles'
import MuiCard, { CardProps } from '@mui/material/Card'
import Typography from '@mui/material/Typography'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout'

import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import {useForm} from "react-hook-form";
import {HttpAuth} from "../../http/user/httpAuth";
import {navigationHelper} from "../../utils/helpers/navigationHelper";
import {ControlledTextFieldFilled} from "../../components/forms";
import {useAction} from "../../hooks/useAction";
import {Stack} from "@mui/material";

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '28rem' }
}))


enum ForgotPasswordFormFields {
  Mail = 'mail'
}

type ForgotPasswordFormData = {
  [ForgotPasswordFormFields.Mail]: string
}

const ForgotPassword = () => {
  const [pressedButton, setPressedButton] = useState<boolean>(false)
  const {snackbarSuccess, snackbarError, showLoader, hideLoader} = useAction();
  const theme = useTheme()
  
  const forgotPasswordFormSchema = yup.object().shape({
    [ForgotPasswordFormFields.Mail]: yup.string().required('Campo obligatorio').email('Mail inválido')
  })
  const {
    control,
    handleSubmit
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordFormSchema),
  });
  const onHandlePasswordRequest = (data: ForgotPasswordFormData) : void => {
    showLoader()

    HttpAuth.forgotPassword(data.mail)
        .then((response) => {
          hideLoader()
          if (response.tieneError) {
              snackbarError(response.descripcionError);
          } else {
              setPressedButton(true)
            setTimeout(() => {
              window.location.href = navigationHelper.to("login");
            }, 7000)
          }
        })
        .catch(() => {
            hideLoader();
            snackbarSuccess("El usuario ingresado no existe o esta inactivo");
        });
  };

  return (
      <Grid container justifyContent={'center'} alignItems={'center'} sx={{height: '100vh !important'}}>
        <Card sx={{ zIndex: 1 }}>
          <CardContent sx={{ p: theme => `${theme.spacing(12, 9, 7)} !important` }}>
            <>
              {
                pressedButton ?
                    <Box sx={{ mb: 6 }}>
                      <Typography variant='h5' sx={{ fontWeight: 600, mb: 1.5 }}>
                        Revise su casilla de email
                      </Typography>
                      <Typography variant='body2'>
                        Debió recibir un mail con las instrucciones para establecer una nueva contraseña.
                      </Typography>
                      <Typography variant='body2'>
                        Dicho mail no debe ser contestado
                      </Typography>
                      <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                        <Link to='/login' style={{
                          display: 'flex',
                          fontSize: '0.875rem',
                          alignItems: 'center',
                          textDecoration: 'none',
                          justifyContent: 'center',
                          color: theme.palette.primary.main
                        }}>
                          <Icon icon='mdi:chevron-left' />
                          <span>Volver al inicio</span>
                        </Link>
                      </Typography>
                    </Box>
                    :
                    <>
                      <Box sx={{ mb: 6 }}>
                        <Typography variant='h5' sx={{ fontWeight: 600, mb: 1.5 }}>
                          ¿Olvidaste tu contraseña? 🔒
                        </Typography>
                        <Typography variant='body2'>
                          Ingrese el mail con el cual se registró y le enviaremos las instrucciones para recuperar la contraseña
                        </Typography>
                      </Box>
                      <form onSubmit={handleSubmit(onHandlePasswordRequest)}>
                          <Stack spacing={2}>
                            <ControlledTextFieldFilled control={control}
                                                       label={'Email'}
                                                       fullWidth
                                                       name={ForgotPasswordFormFields.Mail}
                                                       autoFocus
                            />
                            <Button fullWidth size='large' variant='contained' sx={{ mb: 5.25 }} type="submit">
                              Enviar link
                            </Button>
                            <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Link to='/login' style={{
                                display: 'flex',
                                fontSize: '0.875rem',
                                alignItems: 'center',
                                textDecoration: 'none',
                                justifyContent: 'center',
                                color: theme.palette.primary.main
                              }}>
                                <Icon icon='mdi:chevron-left' />
                                <span>Volver al inicio</span>
                              </Link>
                            </Typography>
                          </Stack>
                      </form>
                    </>
              }
            </>
          </CardContent>
        </Card>
      </Grid>
  )
}

ForgotPassword.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>

ForgotPassword.guestGuard = true

export default ForgotPassword
